export const detectOrderBlock = (
  currencyPair,   // Not used since tickSize and tickValue are removed, but kept for consistency
  data,           // Array of candlestick data
  startDate = null, // Optional: Start date for detection
  imbalanceFactorThreshold = 2.0, // Adjustable threshold for imbalance factor
  rangeDifferenceThreshold = 1.5, // Adjustable threshold for range difference
  candleRange = 10 // Adjustable threshold for range difference
) => {
  console.log("=== detectOrderBlock Function Called ===");
  console.log(`Parameters:
    currencyPair: ${currencyPair},
    startDate: ${startDate},
    imbalanceFactorThreshold: ${imbalanceFactorThreshold},
    rangeDifferenceThreshold: ${rangeDifferenceThreshold},
    candleRange: ${candleRange}
  `);

  const bullishOrderBlocks = [];
  const bearishOrderBlocks = [];

  // Iterate through the data starting from the 10th candle to allow looking back 10 candles
  for (let i = candleRange; i < data.length - candleRange; i++) {
    const orderBlockCandidate = data[i - candleRange];
    const currentCandle = data[i];

    //console.log(`\nEvaluating Candle at index ${i}:`);
    //console.log(`Order Block Candidate Candle [${i - candleRange}]: Time=${orderBlockCandidate.time}, Open=${orderBlockCandidate.open}, Close=${orderBlockCandidate.close}, High=${orderBlockCandidate.high}, Low=${orderBlockCandidate.low}`);
    //console.log(`Current Candle [${i}]: Time=${currentCandle.time}, Open=${currentCandle.open}, Close=${currentCandle.close}, High=${currentCandle.high}, Low=${currentCandle.low}`);

    // If startDate is provided, skip entries before the startDate
    if (startDate && new Date(orderBlockCandidate.time) < new Date(startDate)) {
      console.log(`Skipping order block candidate before startDate: ${startDate}`);
      continue;
    }

    // Check for Imbalance (Simplified - Requires Refinement)
    const previousCandles = data.slice(i - candleRange, i - 1); 
    let isImbalance = false;
    let maxRange = 0;
    let minRange = Infinity;
    let avgRange = 0;
    for (const candle of previousCandles) {
      const range = candle.high - candle.low;
      maxRange = Math.max(maxRange, range);
      minRange = Math.min(minRange, range);
      avgRange += range;
    }
    avgRange /= previousCandles.length; 

    // Calculate Imbalance Factor (Simplified)
    const imbalanceFactor = (orderBlockCandidate.high - orderBlockCandidate.low) / avgRange; 

    // Consider other factors (requires further research and refinement)
    // - Volume within the order block candidate
    // - Price action within the order block candidate (e.g., wick lengths, engulfing patterns)
    // - Market context (trend direction, volatility)
    console.log(`Imbalance Factor: ${imbalanceFactor} > ${imbalanceFactorThreshold}`);
    console.log(`Range Factor: ${(maxRange - minRange)*rangeDifferenceThreshold} > ${(orderBlockCandidate.high - orderBlockCandidate.low)}`);

    if (imbalanceFactor > imbalanceFactorThreshold && orderBlockCandidate.high - orderBlockCandidate.low > (maxRange - minRange) * rangeDifferenceThreshold) { 
      isImbalance = true; 
    }

    // =============================
    //     BULLISH ORDER BLOCK CHECK
    // =============================
    // Criteria:
    // 1. Order Block Candidate shows significant imbalance
    // 2. Current candle breaks above the high of the candidate
    // 3. The break is sustained over the next 10 candles

    if (isImbalance) { 
      const bullishBreakLevel = orderBlockCandidate.high;
      console.log(`Bullish Break Level: ${bullishBreakLevel}`);
      if (currentCandle.close > bullishBreakLevel) {
        console.log(`Bullish Break Detected: Current Close (${currentCandle.close}) > Bullish Break Level (${bullishBreakLevel})`);

        // Check if the break is sustained over the next 10 candles
        let moveSustained = true;
        for (let k = i + 1; k <= i + candleRange; k++) {
          if (k >= data.length) {
            console.log(`Insufficient data to verify sustainability for bullish order block at index ${i}.`);
            moveSustained = false;
            break;
          }
          const nextCandle = data[k];
          if (nextCandle.close <= bullishBreakLevel) {
            console.log(`Movement Not Sustained: Candle at index ${k} Close (${nextCandle.close}) <= Bullish Break Level (${bullishBreakLevel})`);
            moveSustained = false;
            break;
          }
        }

        if (moveSustained) {
          console.log("Bullish Order Block Confirmed and Sustained.");
          bullishOrderBlocks.push({
            time: orderBlockCandidate.time,
            low: orderBlockCandidate.low,
            high: orderBlockCandidate.high,
            open: orderBlockCandidate.open,
            close: orderBlockCandidate.close,
            moveCandleTime: currentCandle.time,
            moveCandleIndex: i,
            gap: currentCandle.close - orderBlockCandidate.high,
          });
        } else {
          console.log("Bullish Order Block Not Sustained.");
        }
      } else {
        console.log(`Bullish Break Not Detected: Current Close (${currentCandle.close}) <= Bullish Break Level (${bullishBreakLevel})`);
      }
    } else {
      console.log("Current Order Block Candidate does not show significant imbalance. Skipping bullish order block detection.");
    }

    // =============================
    //     BEARISH ORDER BLOCK CHECK
    // =============================
    // Criteria:
    // 1. Order Block Candidate shows significant imbalance
    // 2. Current candle breaks below the low of the candidate
    // 3. The break is sustained over the next 10 candles

    if (isImbalance) { 
      const bearishBreakLevel = orderBlockCandidate.low;
      console.log(`Bearish Break Level: ${bearishBreakLevel}`);
      if (currentCandle.close < bearishBreakLevel) {
        console.log(`Bearish Break Detected: Current Close (${currentCandle.close}) < Bearish Break Level (${bearishBreakLevel})`);

        // Check if the break is sustained over the next 10 candles
        let moveSustained = true;
        for (let k = i + 1; k <= i + candleRange; k++) {
          if (k >= data.length) {
            console.log(`Insufficient data to verify sustainability for bearish order block at index ${i}.`);
            moveSustained = false;
            break;
          }
          const nextCandle = data[k];
          if (nextCandle.close >= bearishBreakLevel) {
            console.log(`Movement Not Sustained: Candle at index ${k} Close (${nextCandle.close}) >= Bearish Break Level (${bearishBreakLevel})`);
            moveSustained = false;
            break;
          }
        }

        if (moveSustained) {
          console.log("Bearish Order Block Confirmed and Sustained.");
          bearishOrderBlocks.push({
            time: orderBlockCandidate.time,
            low: orderBlockCandidate.low,
            high: orderBlockCandidate.high,
            open: orderBlockCandidate.open,
            close: orderBlockCandidate.close,
            moveCandleTime: currentCandle.time,
            moveCandleIndex: i,
            gap: orderBlockCandidate.low - currentCandle.close,
          });
        } else {
          console.log("Bearish Order Block Not Sustained.");
        }
      } else {
        console.log(`Bearish Break Not Detected: Current Close (${currentCandle.close}) >= Bearish Break Level (${bearishBreakLevel})`);
      }
    } else {
      console.log("Current Order Block Candidate does not show significant imbalance. Skipping bearish order block detection.");
    }
  }

  // Sort the Order Blocks by time
  bullishOrderBlocks.sort((a, b) => new Date(a.time) - new Date(b.time));
  bearishOrderBlocks.sort((a, b) => new Date(a.time) - new Date(b.time));

  console.log(`\nDetection Complete. Bullish Order Blocks Detected: ${bullishOrderBlocks.length}`);
  console.log(`Bearish Order Blocks Detected: ${bearishOrderBlocks.length}`);

  return {
    bullishOrderBlocks,
    bearishOrderBlocks,
    earliestDate: bullishOrderBlocks.length > 0 ? bullishOrderBlocks[0].time : null,
  };
};